import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { AnsibleVariable } from '@/api/configuration';
import { listProjectDefaultVars, saveProjectDefaultVars } from '@/api/configuration';
import { ConfigGitRepoSettings } from '@/components/admin/config-git-repo-settings';
import { DeployTriggerConfigForm } from '@/components/admin/deploy-trigger-config-form';
import { PlaybookManager } from '@/components/admin/playbook-manager';
import { RepositoryAssetLinks } from '@/components/admin/repository-asset-links';
import { RepositorySelector } from '@/components/admin/repository-selector';
import { SlackIntegration } from '@/components/admin/slack-integration';
import { useAuthStore } from '@/stores/auth-store';

// --- Types ---

type SettingsTab = 'notifications' | 'playbooks' | 'default-vars' | 'config-git-repo' | 'deploy-triggers' | 'asset-links';

const SETTINGS_TABS: { key: SettingsTab; label: string }[] = [
  { key: 'notifications', label: 'Notifications' },
  { key: 'playbooks', label: 'Playbooks' },
  { key: 'default-vars', label: 'Default Variables' },
  { key: 'config-git-repo', label: 'Config Git Repo' },
  { key: 'deploy-triggers', label: 'Deploy Triggers' },
  { key: 'asset-links', label: 'Asset Links' },
];

// --- Access Denied ---

function AccessDenied() {
  const navigate = useNavigate();

  return (
    <main id="main-content" className="max-w-4xl mx-auto px-4 py-6">
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-red-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
        <h1 className="mt-4 text-xl font-semibold text-content">Access Denied</h1>
        <p className="mt-2 text-sm text-content-secondary">
          You do not have permission to access project settings. Only project administrators can manage this page.
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mt-6 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
        >
          Go to Dashboard
        </button>
      </div>
    </main>
  );
}

// --- Default Variables Tab ---

function DefaultVariablesTab({ projectKey }: { projectKey: string }) {
  const [vars, setVars] = useState<AnsibleVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const loadVars = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listProjectDefaultVars(projectKey);
      setVars(data);
    } catch {
      setError('Failed to load project default variables.');
    } finally {
      setLoading(false);
    }
  }, [projectKey]);

  useEffect(() => {
    loadVars();
  }, [loadVars]);

  const handleAdd = () => {
    const key = newKey.trim();
    const value = newValue.trim();
    if (!key) return;
    if (vars.some((v) => v.key === key)) {
      setError(`Variable "${key}" already exists. Remove it first or edit its value.`);
      return;
    }
    setVars([...vars, { key, value }]);
    setNewKey('');
    setNewValue('');
    setError(null);
  };

  const handleRemove = (varKey: string) => {
    setVars(vars.filter((v) => v.key !== varKey));
  };

  const handleValueChange = (varKey: string, newVal: string) => {
    setVars(vars.map((v) => (v.key === varKey ? { ...v, value: newVal } : v)));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await saveProjectDefaultVars(projectKey, vars);
      setVars(saved);
    } catch {
      setError('Failed to save variables.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-content mb-1">Project Default Variables</h3>
        <p className="text-xs text-content-secondary">
          These variables are included as Ansible extra-vars in every playbook run for this project. They can be overridden by environment or playbook-level defaults.
        </p>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 mb-3">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {loading && (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
          ))}
        </div>
      )}

      {!loading && (
        <>
          {/* Existing variables */}
          {vars.length > 0 && (
            <div className="space-y-2 mb-4">
              {vars.map((v) => (
                <div key={v.key} className="flex items-center gap-2">
                  <span className="w-1/3 rounded-md border border-border bg-surface-raised px-3 py-1.5 text-sm text-content font-mono truncate" title={v.key}>
                    {v.key}
                  </span>
                  <input
                    type="text"
                    value={v.value}
                    onChange={(e) => handleValueChange(v.key, e.target.value)}
                    className="flex-1 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemove(v.key)}
                    className="rounded px-2 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                    aria-label={`Remove variable ${v.key}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {vars.length === 0 && (
            <div className="rounded-lg border border-dashed border-border p-4 text-center mb-4">
              <p className="text-sm text-content-tertiary">No project default variables configured.</p>
            </div>
          )}

          {/* Add new variable */}
          <div className="flex items-end gap-2 mb-4">
            <div className="w-1/3">
              <label htmlFor="new-var-key" className="block text-xs font-medium text-content-secondary mb-1">Key</label>
              <input
                id="new-var-key"
                type="text"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="variable_name"
                className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              />
            </div>
            <div className="flex-1">
              <label htmlFor="new-var-value" className="block text-xs font-medium text-content-secondary mb-1">Value</label>
              <input
                id="new-var-value"
                type="text"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder="value"
                className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              />
            </div>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!newKey.trim()}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
            >
              Add
            </button>
          </div>

          {/* Save button */}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
          >
            {saving ? 'Saving...' : 'Save Variables'}
          </button>
        </>
      )}
    </div>
  );
}

// --- Deploy Triggers Tab ---

function DeployTriggersTabPanel({ projectKey }: { projectKey: string }) {
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);

  return (
    <div>
      <RepositorySelector
        projectKey={projectKey}
        selectedRepo={selectedRepo}
        onRepoChange={setSelectedRepo}
      />
      {selectedRepo && <DeployTriggerConfigForm repoName={selectedRepo} />}
    </div>
  );
}

// --- Asset Links Tab ---

function AssetLinksTabPanel({ projectKey }: { projectKey: string }) {
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);

  return (
    <div>
      <RepositorySelector
        projectKey={projectKey}
        selectedRepo={selectedRepo}
        onRepoChange={setSelectedRepo}
      />
      {selectedRepo && <RepositoryAssetLinks repoName={selectedRepo} />}
    </div>
  );
}

// --- Main Page ---

export function ProjectSettingsPage() {
  const { projectKey } = useParams<{ projectKey: string }>();
  const key = projectKey ?? '';
  const user = useAuthStore((state) => state.user);

  const [activeTab, setActiveTab] = useState<SettingsTab>('notifications');

  // Check admin access: global Admin OR project-level Admin
  const isAdmin =
    user?.role === 'Admin' ||
    (user?.projectRoles ?? []).some(
      (entry) => entry.project === key && entry.role === 'Admin'
    );

  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <main id="main-content" className="mx-auto max-w-5xl">
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-content">Settings</h1>
        <p className="mt-1 text-sm text-content-secondary">
          Project configuration and integrations
        </p>
      </header>

      {/* Horizontal tab bar */}
      <nav aria-label="Settings sections" className="mb-4">
        <div className="border-b border-border">
          <ul className="flex gap-0 -mb-px" role="tablist">
            {SETTINGS_TABS.map((tab) => (
              <li key={tab.key} role="presentation">
                <button
                  role="tab"
                  id={'settings-tab-' + tab.key}
                  aria-selected={activeTab === tab.key}
                  aria-controls={'settings-panel-' + tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={[
                    'inline-block px-4 py-2.5 text-sm font-medium border-b-2 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2',
                    activeTab === tab.key
                      ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300'
                      : 'border-transparent text-content-secondary hover:text-content hover:border-border',
                  ].join(' ')}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Tab content */}
      <div
        id={'settings-panel-' + activeTab}
        role="tabpanel"
        aria-labelledby={'settings-tab-' + activeTab}
      >
        {activeTab === 'notifications' && <SlackIntegration projectKey={key} />}
        {activeTab === 'playbooks' && <PlaybookManager projectKey={key} />}
        {activeTab === 'default-vars' && <DefaultVariablesTab projectKey={key} />}
        {activeTab === 'config-git-repo' && <ConfigGitRepoSettings projectKey={key} />}
        {activeTab === 'asset-links' && <AssetLinksTabPanel projectKey={key} />}
        {activeTab === 'deploy-triggers' && <DeployTriggersTabPanel projectKey={key} />}
      </div>
    </main>
  );
}